// App.js или index.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import "./App.css";

const API_URL = "https://api.tg-merengafood.modvik.ru";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkUser = async () => {
    await fetch(`${API_URL}/v1/chat/getChats`);
    try {
      console.log("Telegram initData:");
      if (window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        tg.ready();

        const initData = tg.initData;
        console.log("Telegram initData:", initData);

        let cleanInitData = initData;

        if (initData.includes("\n")) {
          const parts = initData.split("\n");
          cleanInitData = parts[0];
          console.log(
            "Found multiline initData, using first part:",
            cleanInitData
          );
        }

        cleanInitData = cleanInitData.trim();

        if (cleanInitData) {
          const response = await fetch(`${API_URL}/v1/chat/login`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ initData: cleanInitData }),
          });

          const data = await response.json();

          if (data.authenticated) {
            setIsAuthenticated(true);
            setUserData(data.user);

            tg.expand();
            tg.enableClosingConfirmation();
          } else {
            setIsAuthenticated(false);
          }
        } else {
          console.log("No initData available");
          setIsAuthenticated(false);
        }
      } else {
        console.log("Not in Telegram WebApp");
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error("Authentication error:", error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkUser();
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Загрузка админ-панели...</p>
      </div>
    );
  }

  return (
    <div className="App">
      {isAuthenticated ? (
        <AdminApp userData={userData} />
      ) : (
        <UnauthorizedView />
      )}
    </div>
  );
}

function AdminApp({ userData }) {
  const [view, setView] = useState("chats");
  const [selectedChat, setSelectedChat] = useState(null);
  const [sendError, setSendError] = useState(null);

  return (
    <div className="admin-app">
      <header className="admin-header">
        {view === "chats" ? (
          <div className="header-content">
            <h1>💬 Админ-панель чатов</h1>
            <p className="admin-info">
              Вы вошли как: {userData?.first_name} {userData?.last_name}
            </p>
          </div>
        ) : (
          <div className="chat-header">
            <button
              className="back-button"
              onClick={() => {
                setView("chats");
                setSendError(null);
              }}
            >
              ← Назад
            </button>
            <div className="chat-title">
              <h2>Чат с пользователем</h2>
              {selectedChat && (
                <p className="chat-subtitle">
                  ID: {selectedChat.id} | Telegram: {selectedChat.telegram_id}
                </p>
              )}
            </div>
          </div>
        )}
      </header>

      <div className="content">
        {view === "chats" ? (
          <ChatsList
            onSelectChat={(chat) => {
              setSelectedChat(chat);
              setView("chat");
              setSendError(null);
            }}
          />
        ) : (
          <AdminChatWindow
            chat={selectedChat}
            adminId={userData?.id}
            sendError={sendError}
            setSendError={setSendError}
          />
        )}
      </div>
    </div>
  );
}

function ChatsList({ onSelectChat }) {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [unreadCounts, setUnreadCounts] = useState({});

  const fetchChats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/v1/chat/getChats`);

      if (!response.ok) {
        throw new Error("Failed to fetch chats");
      }

      const data = await response.json();
      
      // Обрабатываем новую структуру данных
      const formattedChats = data.map(item => ({
        id: item.chat.id,
        telegram_id: item.chat.telegram_id,
        user_id: item.chat.user_id,
        is_active: item.chat.is_active,
        created_at: item.chat.created_at,
        updated_at: item.chat.updated_at,
        last_message_at: item.chat.last_message_at,
        user_name: item.user_info.first_name,
        user_last_name: item.user_info.last_name,
        username: item.user_info.username
      }));

      const sortedChats = [...formattedChats].sort(
        (a, b) => new Date(b.last_message_at) - new Date(a.last_message_at)
      );

      setChats(sortedChats);
      setError(null);

      // Подсчитываем непрочитанные сообщения для каждого чата
      const counts = {};
      for (const chat of sortedChats) {
        const unread = await getUnreadCount(chat.id);
        counts[chat.id] = unread;
      }
      setUnreadCounts(counts);
    } catch (err) {
      console.error("Error fetching chats:", err);
      setError("Не удалось загрузить чаты");
    } finally {
      setLoading(false);
    }
  }, []);

  const getUnreadCount = async (chatId) => {
    try {
      const response = await fetch(
        `${API_URL}/v1/chat/getChat?chat_id=${chatId}`
      );
      if (!response.ok) return 0;

      const messages = await response.json();
      // Считаем непрочитанные сообщения от пользователя (is_from_user: true, is_read: false)
      return messages.filter((msg) => msg.is_from_user && !msg.is_read).length;
    } catch {
      return 0;
    }
  };

  const markChatAsRead = async (chatId) => {
    try {
      await fetch(`${API_URL}/v1/chat/readMessage?chat_id=${chatId}`, {
        method: "GET",
      });

      // Обновляем счетчик непрочитанных
      setUnreadCounts((prev) => ({
        ...prev,
        [chatId]: 0,
      }));
    } catch (err) {
      console.error("Error marking chat as read:", err);
    }
  };

  useEffect(() => {
    fetchChats();

    const interval = setInterval(fetchChats, 30000);
    return () => clearInterval(interval);
  }, [fetchChats]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Сегодня ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else if (diffDays === 1) {
      return `Вчера ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else if (diffDays < 7) {
      return `${date.toLocaleDateString([], {
        weekday: "long",
      })} ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else {
      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    }
  };

  const filteredChats = chats.filter((chat) => {
    if (filter === "all") return true;
    if (filter === "active") return chat.is_active;
    if (filter === "inactive") return !chat.is_active;
    return true;
  });

  const handleChatClick = async (chat) => {
    // Помечаем чат как прочитанный при клике
    if (unreadCounts[chat.id] > 0) {
      await markChatAsRead(chat.id);
    }
    onSelectChat(chat);
  };

  if (loading) {
    return (
      <div className="chats-loading">
        <div className="spinner"></div>
        <p>Загрузка списка чатов...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button onClick={fetchChats}>Повторить</button>
      </div>
    );
  }

  return (
    <div className="chats-container">
      <div className="chats-filter">
        <button
          className={`filter-btn ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          Все
        </button>
        <button
          className={`filter-btn ${filter === "active" ? "active" : ""}`}
          onClick={() => setFilter("active")}
        >
          Активные
        </button>
        <button
          className={`filter-btn ${filter === "inactive" ? "active" : ""}`}
          onClick={() => setFilter("inactive")}
        >
          Закрытые
        </button>
      </div>

      <div className="chats-list">
        {filteredChats.length === 0 ? (
          <div className="no-chats">
            <p>Нет чатов</p>
            {filter !== "all" && <p>Попробуйте изменить фильтр</p>}
          </div>
        ) : (
          filteredChats.map((chat) => {
            const unreadCount = unreadCounts[chat.id] || 0;

            return (
              <div
                key={chat.id}
                className={`chat-item ${
                  chat.is_active ? "active" : "inactive"
                } ${unreadCount > 0 ? "has-unread" : ""}`}
                onClick={() => handleChatClick(chat)}
              >
                <div className="chat-avatar">
                  <span>👤</span>
                  {unreadCount > 0 && (
                    <div className="avatar-notification"></div>
                  )}
                </div>
                <div className="chat-info">
                  <div className="chat-header">
                    <h3>{chat.user_name || `Пользователь #${chat.telegram_id}`}</h3>
                    <span className="chat-time">
                      {formatDate(chat.last_message_at)}
                    </span>
                  </div>
                  <div className="chat-meta">
                    <span
                      className={`chat-status ${
                        chat.is_active ? "status-active" : "status-inactive"
                      }`}
                    >
                      {chat.is_active ? "🟢 Активен" : "🔴 Закрыт"}
                    </span>
                    <span className="chat-id">Чат #{chat.id}</span>
                  </div>
                </div>
                {unreadCount > 0 && (
                  <div className="unread-badge">{unreadCount}</div>
                )}
                <div className="chat-arrow">›</div>
              </div>
            );
          })
        )}
      </div>

      <div className="chats-stats">
        <p>
          Всего чатов: {chats.length} | Активных:{" "}
          {chats.filter((c) => c.is_active).length} | Непрочитанных:{" "}
          {Object.values(unreadCounts).reduce((sum, count) => sum + count, 0)}
        </p>
      </div>
    </div>
  );
}

function AdminChatWindow({ chat, adminId, sendError, setSendError }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [nameEditStatus, setNameEditStatus] = useState({ type: "", message: "" });
  const messagesEndRef = useRef(null);
  const hasMarkedAsRead = useRef(false);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/v1/chat/getChat?chat_id=${chat.id}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch messages");
      }

      const data = await response.json();
      setMessages(data);
      setError(null);

      // Помечаем сообщения как прочитанные при загрузке
      if (
        !hasMarkedAsRead.current &&
        data.some((msg) => msg.is_from_user && !msg.is_read)
      ) {
        markMessagesAsRead();
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
      setError("Не удалось загрузить сообщения");
    } finally {
      setLoading(false);
    }
  }, [chat.id]);

  const markMessagesAsRead = async () => {
    try {
      const response = await fetch(
        `${API_URL}/v1/chat/readMessage?chat_id=${chat.id}`
      );
      if (response.ok) {
        hasMarkedAsRead.current = true;
        console.log("Messages marked as read");
      }
    } catch (err) {
      console.error("Error marking messages as read:", err);
    }
  };

  const handleEditName = async () => {
    if (!editedName.trim()) {
      setNameEditStatus({
        type: "error",
        message: "Имя не может быть пустым"
      });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/v1/chat/editName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          telegram_id: chat.telegram_id,
          name: editedName.trim()
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось обновить имя");
      }

      // Обновляем имя в объекте чата
      chat.user_name = editedName.trim();
      
      setNameEditStatus({
        type: "success",
        message: "Имя успешно обновлено"
      });
      setIsEditingName(false);
      
      // Через 3 секунды скрываем сообщение об успехе
      setTimeout(() => {
        setNameEditStatus({ type: "", message: "" });
      }, 3000);

    } catch (err) {
      console.error("Error editing name:", err);
      setNameEditStatus({
        type: "error",
        message: err.message
      });
    }
  };

  const startEditingName = () => {
    setEditedName(chat.user_name || "");
    setIsEditingName(true);
    setNameEditStatus({ type: "", message: "" });
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
    setEditedName("");
    setNameEditStatus({ type: "", message: "" });
  };

  useEffect(() => {
    fetchMessages();
    hasMarkedAsRead.current = false;

    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    scrollToBottom();

    // Помечаем как прочитанные при открытии чата
    if (messages.some((msg) => msg.is_from_user && !msg.is_read)) {
      markMessagesAsRead();
    }
  }, [messages]);

  useEffect(() => {
    // Очищаем ошибку при изменении чата
    setSendError(null);
    setIsEditingName(false);
    setNameEditStatus({ type: "", message: "" });
  }, [chat.id, setSendError]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      setSendError(null);

      const response = await fetch(`${API_URL}/v1/chat/sendMessage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: chat.user_id,
          chat_id: chat.id,
          text: newMessage.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Проверяем структуру ошибки от сервера
        if (data.error) {
          throw new Error(data.error);
        } else {
          throw new Error("Failed to send message");
        }
      }

      // Добавляем сообщение в список
      setMessages((prev) => [...prev, data]);
      setNewMessage("");

      // Обновляем список сообщений
      setTimeout(fetchMessages, 500);
    } catch (err) {
      console.error("Error sending message:", err);

      let errorMessage = "Не удалось отправить сообщение";

      // Анализируем текст ошибки
      if (err.message.includes("Пользователь заблокировал доступ к боту")) {
        errorMessage = "Пользователь заблокировал бота. Чат деактивирован.";
        // Обновляем статус чата
        chat.is_active = false;
      } else if (err.message.includes("Не найден пользователь")) {
        errorMessage = "Пользователь не найден";
      } else {
        errorMessage = err.message;
      }

      setSendError(errorMessage);

      // Если пользователь заблокировал бота, обновляем данные
      if (errorMessage.includes("заблокировал")) {
        setTimeout(() => {
          window.location.reload(); // Перезагружаем для обновления статуса чатов
        }, 2000);
      }
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatMessageDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const groupMessagesByDate = () => {
    const groups = {};

    messages.forEach((message) => {
      const date = new Date(message.created_at).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });

    return groups;
  };

  if (loading && messages.length === 0) {
    return (
      <div className="chat-loading">
        <div className="spinner"></div>
        <p>Загрузка истории сообщений...</p>
      </div>
    );
  }

  const messageGroups = groupMessagesByDate();

  return (
    <div className="admin-chat-window">
      <div className="chat-actions">
        <button
          className="refresh-button"
          onClick={() => {
            fetchMessages();
            setSendError(null);
          }}
          title="Обновить"
        >
          ↻ Обновить
        </button>
        <div className="chat-user-info">
          <div style={{ display: "flex", flexDirection: "column" }}>
            {isEditingName ? (
              <>
                <div className="name-edit-form">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="name-edit-input"
                    placeholder="Введите имя"
                    autoFocus
                  />
                  <div className="name-edit-buttons">
                    <button
                      className="name-edit-btn save"
                      onClick={handleEditName}
                    >
                      Сохранить
                    </button>
                    <button
                      className="name-edit-btn cancel"
                      onClick={cancelEditingName}
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="user-name-display">
                <div>
                  <h3>{chat.user_name || `Пользователь #${chat.telegram_id}`}</h3>
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    Telegram ID: {chat.telegram_id}
                  </div>
                </div>
                <button
                  className="name-edit-btn edit"
                  onClick={startEditingName}
                  title="Редактировать имя"
                >
                  ✏️
                </button>
              </div>
            )}
            {nameEditStatus.message && (
              <div 
                className={`name-edit-status ${nameEditStatus.type} ${nameEditStatus.message ? 'show' : ''}`}
              >
                {nameEditStatus.message}
              </div>
            )}
          </div>
          <span
            className={`chat-status-indicator ${
              chat.is_active ? "active" : "inactive"
            }`}
          >
            {chat.is_active ? "🟢 Активен" : "🔴 Закрыт"}
          </span>
        </div>
      </div>

      {sendError && (
        <div className="send-error">
          <p>⚠️ {sendError}</p>
          <button onClick={() => setSendError(null)}>×</button>
        </div>
      )}

      {error && (
        <div className="chat-error">
          <p>{error}</p>
          <button onClick={fetchMessages}>Повторить</button>
        </div>
      )}

      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>Нет сообщений в этом чате</p>
            <p>Напишите первое сообщение пользователю</p>
          </div>
        ) : (
          Object.entries(messageGroups).map(([date, dateMessages]) => (
            <div key={date}>
              <div className="date-divider">
                <span>
                  {new Date(date).toLocaleDateString("ru-RU", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
              {dateMessages.map((message) => (
                <div
                  key={message.id}
                  className={`message-wrapper ${
                    message.is_from_user ? "user" : "admin"
                  }`}
                >
                  <div className="message">
                    <div className="message-sender">
                      {message.is_from_user ? (
                        <>
                          👤 {chat.user_name || "Пользователь"}
                          {!message.is_read && (
                            <span className="unread-indicator">●</span>
                          )}
                        </>
                      ) : (
                        "🛡️ Вы (Админ)"
                      )}
                    </div>
                    <div className="message-content">
                      <p>{message.text}</p>
                      <span className="message-time">
                        {formatMessageTime(message.created_at)}
                        {!message.is_from_user && message.is_read && " ✓✓"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="message-form" onSubmit={handleSendMessage}>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => {
            setNewMessage(e.target.value);
            if (sendError) setSendError(null);
          }}
          placeholder={
            chat.is_active
              ? "Введите сообщение для пользователя..."
              : "Чат закрыт. Отправка невозможна"
          }
          disabled={sending || !chat.is_active}
          autoFocus
        />
        <button
          type="submit"
          disabled={!newMessage.trim() || sending || !chat.is_active}
          className={sending ? "sending" : ""}
          title={!chat.is_active ? "Чат закрыт" : "Отправить сообщение"}
        >
          {sending ? (
            <>
              <span className="sending-spinner"></span>
            </>
          ) : !chat.is_active ? (
            "❌"
          ) : (
            "➤"
          )}
        </button>
      </form>
    </div>
  );
}

function UnauthorizedView() {
  return (
    <div className="unauthorized">
      <div className="unauthorized-content">
        <h1>🚫 Доступ запрещен</h1>
        <div className="unauthorized-message">
          <p>Эта панель доступна только администраторам.</p>
          <p>
            Пожалуйста, войдите через Telegram Mini App с правами
            администратора.
          </p>
        </div>
        <div className="unauthorized-instructions">
          <h3>Если вы администратор:</h3>
          <ol>
            <li>Откройте приложение через Telegram</li>
            <li>Убедитесь, что у вас есть права администратора</li>
            <li>Если проблема сохраняется, свяжитесь с разработчиком</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default App;